import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || '';

const normalizeText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map((block) => block.children?.map((child) => child.text).join('') || '').join('\n');
  }
  return '';
};

const buildAssetUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url}`;
};

const getImageUrl = (product) => {
  if (product.Images?.[0]?.url) {
    return buildAssetUrl(product.Images[0].url);
  }
  return null;
};

const detailCopy = {
  en: {
    back: 'Back to products',
    notFound: 'Product not found',
    description: 'Product Description',
    noDescription: 'Please contact us for specifications, packing details and quotation.',
    specifications: 'Sourcing Notes',
    inquiry: 'Send Inquiry',
    contact: 'Contact Us',
    emailLabel: 'Sales email',
    category: 'Category',
    exportReady: 'Export-ready product',
    highlights: ['Custom packaging available', 'Sample support before order', 'Mixed container discussion welcome']
  },
  zh: {
    back: '返回产品列表',
    notFound: '产品未找到',
    description: '产品描述',
    noDescription: '请联系我们获取规格、包装和报价详情。',
    specifications: '采购要点',
    inquiry: '立即询价',
    contact: '联系我们',
    emailLabel: '销售邮箱',
    category: '分类',
    exportReady: '适合出口供应',
    highlights: ['支持定制包装', '大货前可沟通样品', '支持混柜方案沟通']
  },
  es: {
    back: 'Volver a productos',
    notFound: 'Producto no encontrado',
    description: 'Descripción del producto',
    noDescription: 'Contáctenos para especificaciones, embalaje y cotización.',
    specifications: 'Notas de compra',
    inquiry: 'Enviar consulta',
    contact: 'Contacto',
    emailLabel: 'Email de ventas',
    category: 'Categoría',
    exportReady: 'Producto listo para exportación',
    highlights: ['Embalaje personalizado', 'Soporte de muestra', 'Contenedor mixto disponible']
  },
  de: {
    back: 'Zurück zu Produkten',
    notFound: 'Produkt nicht gefunden',
    description: 'Produktbeschreibung',
    noDescription: 'Kontaktieren Sie uns für Spezifikationen, Verpackung und Angebot.',
    specifications: 'Beschaffungshinweise',
    inquiry: 'Anfrage senden',
    contact: 'Kontakt',
    emailLabel: 'Vertriebs-E-Mail',
    category: 'Kategorie',
    exportReady: 'Exportfähiges Produkt',
    highlights: ['Individuelle Verpackung', 'Muster vor Bestellung', 'Mischcontainer möglich']
  }
};

const ProductDetail = ({ products, lang = 'en', siteEmail = 'sales@lumenoutdoor.com' }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = detailCopy[lang] || detailCopy.en;
  const product = products.find((item) => String(item.id) === String(id));
  const [imageFailed, setImageFailed] = useState(false);

  if (!product) {
    return (
      <div className="detail-container">
        <div className="detail-card detail-empty">
          <h2>{t.notFound}</h2>
          <button onClick={() => navigate('/')} className="back-btn" type="button">{t.back}</button>
        </div>
      </div>
    );
  }

  const imageUrl = getImageUrl(product);
  const productName = product.displayName || product.Name || 'Product';
  const description = product.displayDescription || normalizeText(product.Description);
  const category = product.categories?.[0]?.Name || product.categories?.[0]?.name || t.exportReady;
  const subject = encodeURIComponent(`Inquiry for ${productName}`);

  return (
    <div className="detail-container">
      <div className="detail-card">
        <button onClick={() => navigate('/')} className="back-btn" type="button">
          {t.back}
        </button>

        <div className="detail-content">
          <div className="detail-image">
            {imageUrl && !imageFailed ? (
              <img src={imageUrl} alt={productName} onError={() => setImageFailed(true)} />
            ) : (
              <div className="detail-image-placeholder">{productName.slice(0, 2).toUpperCase()}</div>
            )}
          </div>

          <div className="detail-info">
            <span className="detail-category">{t.category}: {category}</span>
            <h1 className="detail-title">{productName}</h1>
            {product.price && <div className="detail-price">${product.price}</div>}
            <div className="detail-description">
              <h3>{t.description}</h3>
              <p>{description || t.noDescription}</p>
            </div>
            {product.specs?.length > 0 && (
              <div className="detail-description">
                <h3>{t.specifications}</h3>
                <ul className="detail-specs">
                  {product.specs.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>
            )}
            <ul className="detail-highlights">
              {t.highlights.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <p className="detail-email"><strong>{t.emailLabel}:</strong> <a href={`mailto:${siteEmail}`}>{siteEmail}</a></p>
            <div className="detail-actions">
              <a className="inquiry-btn" href={`mailto:${siteEmail}?subject=${subject}`}>{t.inquiry}</a>
              <a className="contact-btn" href={`mailto:${siteEmail}`}>{t.contact}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
